<template>
  <div class="products-page">
    <section class="products-header">
      <div class="container">
        <h1>{{ categoryTitle }}</h1>
        <p>
          Discover our premium collection of {{ categoryTitle.toLowerCase() }}
        </p>
      </div>
    </section>

    <section class="filters-section">
      <div class="container">
        <div class="filters-container">
          <!-- Search -->
          <div class="search-box">
            <input
              type="text"
              placeholder="Search products..."
              v-model="searchQuery"
              @input="filterProducts"
            />
          </div>

          <!-- Category Filter -->
          <div class="filter-group">
            <label>Category:</label>
            <select v-model="selectedCategory" @change="filterProducts">
              <option value="">All Categories</option>
              <option value="mattresses">Mattresses</option>
              <option value="pillows">Pillows</option>
              <option value="bedding">Bedding</option>
              <option value="bases">Bases</option>
              <option value="bundles">Bundles</option>
            </select>
          </div>

          <!-- Price Filter -->
          <div class="filter-group">
            <label>Price Range:</label>
            <select v-model="priceRange" @change="filterProducts">
              <option value="">All Prices</option>
              <option value="0-100">$0 - $100</option>
              <option value="100-500">$100 - $500</option>
              <option value="500-1000">$500 - $1,000</option>
              <option value="1000+">$1,000+</option>
            </select>
          </div>

          <!-- Sort -->
          <div class="filter-group">
            <label>Sort by:</label>
            <select v-model="sortBy" @change="filterProducts">
              <option value="featured">Featured</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
              <option value="name">Name: A to Z</option>
            </select>
          </div>
        </div>
      </div>
    </section>

    <section class="products-section">
      <div class="container">
        <!-- Loading state -->
        <div v-if="loading" class="loading">
          <h3>Loading products...</h3>
          <p>Please wait while we fetch the latest products.</p>
        </div>

        <!-- Error state -->
        <div v-else-if="error" class="error">
          <h3>Error loading products</h3>
          <p>{{ error }}</p>
          <button @click="loadProducts" class="btn">Try Again</button>
        </div>

        <!-- No products found -->
        <div v-else-if="filteredProducts.length === 0" class="no-products">
          <h3>No products found</h3>
          <p>Try adjusting your filters or search terms.</p>
        </div>

        <!-- Products grid -->
        <div v-else class="products-grid">
          <div
            class="product-card"
            v-for="product in filteredProducts"
            :key="product._id || product.id"
          >
            <div class="product-image">
              <img
                :src="product.images?.[0]?.url || product.image"
                :alt="product.name"
                loading="lazy"
              />
              <div v-if="product.isFeatured" class="featured-badge">
                Featured
              </div>
              <div
                v-if="
                  product.originalPrice && product.originalPrice > product.price
                "
                class="discount-badge"
              >
                -{{
                  Math.round(
                    ((product.originalPrice - product.price) /
                      product.originalPrice) *
                      100
                  )
                }}%
              </div>
            </div>
            <div class="product-card-content">
              <div class="product-header">
                <h4>{{ product.name }}</h4>
                <div class="product-meta">
                  <span v-if="product.sku" class="sku">{{ product.sku }}</span>
                  <span v-if="product.rating?.count > 0" class="rating">
                    ★ {{ product.rating.average.toFixed(1) }}
                  </span>
                </div>
              </div>
              <p class="description">
                {{ product.shortDescription || product.description }}
              </p>
              <div class="pricing">
                <span class="current-price"
                  >${{ product.price.toLocaleString() }}</span
                >
                <span
                  v-if="
                    product.originalPrice &&
                    product.originalPrice > product.price
                  "
                  class="original-price"
                >
                  ${{ product.originalPrice.toLocaleString() }}
                </span>
              </div>
              <div class="product-actions">
                <router-link
                  :to="`/product/${product._id || product.id}`"
                  class="btn btn-outline"
                >
                  View Details
                </router-link>
                <button
                  @click="addToCart(product)"
                  class="btn"
                  :disabled="!product.isActive"
                >
                  {{ product.isActive ? "Add to Cart" : "Out of Stock" }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from "vue";
import { useRoute } from "vue-router";
import { useCartStore } from "../stores/cart";
import { useProductsStore } from "../stores/products";

export default {
  name: "Products",
  setup() {
    const route = useRoute();
    const cartStore = useCartStore();
    const productsStore = useProductsStore();

    // Reactive data
    const searchQuery = ref("");
    const selectedCategory = ref("");
    const priceRange = ref("");
    const sortBy = ref("featured");

    // Product data - fallback to static data if API fails
    const staticProducts = ref([
      // Mattresses
      {
        id: "mattress-1",
        name: "The Original Mattress",
        description: "Our signature mattress with premium memory foam.",
        shortDescription:
          "Premium memory foam mattress with advanced cooling technology.",
        price: 995,
        originalPrice: 1295,
        image:
          "https://images.unsplash.com/photo-1616627781431-d8a886095a74?q=80&w=1887&auto=format&fit=crop",
        images: [
          {
            url: "https://images.unsplash.com/photo-1616627781431-d8a886095a74?q=80&w=1887&auto=format&fit=crop",
            alt: "The Original Mattress",
          },
        ],
        category: "mattresses",
        sku: "MAT-001",
        isActive: true,
        isFeatured: true,
        rating: { average: 4.8, count: 127 },
      },
      {
        id: "mattress-2",
        name: "The Hybrid Mattress",
        description:
          "Combines memory foam with pocket springs for ultimate comfort.",
        shortDescription:
          "Memory foam + pocket springs for the perfect balance.",
        price: 1295,
        image:
          "https://images.unsplash.com/photo-1617347442247-0d3408019a6d?q=80&w=1887&auto=format&fit=crop",
        images: [
          {
            url: "https://images.unsplash.com/photo-1617347442247-0d3408019a6d?q=80&w=1887&auto=format&fit=crop",
            alt: "The Hybrid Mattress",
          },
        ],
        category: "mattresses",
        sku: "MAT-002",
        isActive: true,
        isFeatured: false,
        rating: { average: 4.6, count: 89 },
      },
      {
        id: "mattress-3",
        name: "The Latex Mattress",
        description: "Natural latex for superior support and breathability.",
        shortDescription: "100% natural latex for eco-friendly comfort.",
        price: 1595,
        image:
          "https://images.unsplash.com/photo-1602161426214-771f280b435f?q=80&w=1887&auto=format&fit=crop",
        images: [
          {
            url: "https://images.unsplash.com/photo-1602161426214-771f280b435f?q=80&w=1887&auto=format&fit=crop",
            alt: "The Latex Mattress",
          },
        ],
        category: "mattresses",
        sku: "MAT-003",
        isActive: true,
        isFeatured: false,
        rating: { average: 4.9, count: 156 },
      },
      {
        id: "mattress-4",
        name: "The Cooling Mattress",
        description: "Advanced cooling technology for temperature regulation.",
        shortDescription:
          "Advanced cooling technology for temperature regulation.",
        price: 1195,
        originalPrice: 1495,
        image:
          "https://images.unsplash.com/photo-1616627781431-d8a886095a74?q=80&w=1887&auto=format&fit=crop",
        images: [
          {
            url: "https://images.unsplash.com/photo-1616627781431-d8a886095a74?q=80&w=1887&auto=format&fit=crop",
            alt: "The Cooling Mattress",
          },
        ],
        category: "mattresses",
        sku: "MAT-004",
        isActive: true,
        isFeatured: true,
        rating: { average: 4.7, count: 203 },
      },
      // Pillows
      {
        id: "pillow-1",
        name: "Memory Foam Pillow",
        description: "Contours to your head and neck for optimal support.",
        shortDescription: "Memory foam that contours to your head and neck.",
        price: 89,
        image:
          "https://images.unsplash.com/photo-1542359649-31e03cdde4fe?q=80&w=2070&auto=format&fit=crop",
        images: [
          {
            url: "https://images.unsplash.com/photo-1542359649-31e03cdde4fe?q=80&w=2070&auto=format&fit=crop",
            alt: "Memory Foam Pillow",
          },
        ],
        category: "pillows",
        sku: "PIL-001",
        isActive: true,
        isFeatured: false,
        rating: { average: 4.5, count: 67 },
      },
      {
        id: "pillow-2",
        name: "Down Alternative Pillow",
        description:
          "Hypoallergenic alternative to down with cloud-like softness.",
        shortDescription: "Hypoallergenic alternative to down pillows.",
        price: 65,
        image:
          "https://images.unsplash.com/photo-1542359649-31e03cdde4fe?q=80&w=2070&auto=format&fit=crop",
        images: [
          {
            url: "https://images.unsplash.com/photo-1542359649-31e03cdde4fe?q=80&w=2070&auto=format&fit=crop",
            alt: "Down Alternative Pillow",
          },
        ],
        category: "pillows",
        sku: "PIL-002",
        isActive: true,
        isFeatured: false,
        rating: { average: 4.3, count: 45 },
      },
      {
        id: "pillow-3",
        name: "Cooling Gel Pillow",
        description: "Gel-infused memory foam for cool, comfortable sleep.",
        shortDescription: "Gel-infused memory foam for cool sleep.",
        price: 95,
        image:
          "https://images.unsplash.com/photo-1542359649-31e03cdde4fe?q=80&w=2070&auto=format&fit=crop",
        images: [
          {
            url: "https://images.unsplash.com/photo-1542359649-31e03cdde4fe?q=80&w=2070&auto=format&fit=crop",
            alt: "Cooling Gel Pillow",
          },
        ],
        category: "pillows",
        sku: "PIL-003",
        isActive: true,
        isFeatured: true,
        rating: { average: 4.6, count: 78 },
      },
      // Bedding
      {
        id: "bedding-1",
        name: "Silk Pillowcase Set",
        description: "Luxurious silk pillowcases for hair and skin protection.",
        shortDescription: "Luxurious silk pillowcases for hair protection.",
        price: 45,
        image:
          "https://images.unsplash.com/photo-1616486595539-0b8d8c3614da?q=80&w=1887&auto=format&fit=crop",
        images: [
          {
            url: "https://images.unsplash.com/photo-1616486595539-0b8d8c3614da?q=80&w=1887&auto=format&fit=crop",
            alt: "Silk Pillowcase Set",
          },
        ],
        category: "bedding",
        sku: "BED-001",
        isActive: true,
        isFeatured: false,
        rating: { average: 4.4, count: 34 },
      },
      {
        id: "bedding-2",
        name: "Egyptian Cotton Sheets",
        description: "Premium 1000 thread count Egyptian cotton sheets.",
        shortDescription: "Premium 1000 thread count Egyptian cotton.",
        price: 120,
        image:
          "https://images.unsplash.com/photo-1616486595539-0b8d8c3614da?q=80&w=1887&auto=format&fit=crop",
        images: [
          {
            url: "https://images.unsplash.com/photo-1616486595539-0b8d8c3614da?q=80&w=1887&auto=format&fit=crop",
            alt: "Egyptian Cotton Sheets",
          },
        ],
        category: "bedding",
        sku: "BED-002",
        isActive: true,
        isFeatured: true,
        rating: { average: 4.8, count: 156 },
      },
      {
        id: "bedding-3",
        name: "Comfortable Duvet Cover",
        description: "Soft, breathable duvet cover for year-round comfort.",
        shortDescription:
          "Soft, breathable duvet cover for year-round comfort.",
        price: 85,
        image:
          "https://images.unsplash.com/photo-1616486595539-0b8d8c3614da?q=80&w=1887&auto=format&fit=crop",
        images: [
          {
            url: "https://images.unsplash.com/photo-1616486595539-0b8d8c3614da?q=80&w=1887&auto=format&fit=crop",
            alt: "Comfortable Duvet Cover",
          },
        ],
        category: "bedding",
        sku: "BED-003",
        isActive: true,
        isFeatured: false,
        rating: { average: 4.2, count: 23 },
      },
      // Bases
      {
        id: "base-1",
        name: "Adjustable Base",
        description: "Electric adjustable base with massage and USB ports.",
        shortDescription: "Electric adjustable base with massage function.",
        price: 795,
        image:
          "https://images.unsplash.com/photo-1588072683812-573523f769d9?q=80&w=1887&auto=format&fit=crop",
        images: [
          {
            url: "https://images.unsplash.com/photo-1588072683812-573523f769d9?q=80&w=1887&auto=format&fit=crop",
            alt: "Adjustable Base",
          },
        ],
        category: "bases",
        sku: "BAS-001",
        isActive: true,
        isFeatured: true,
        rating: { average: 4.7, count: 89 },
      },
      {
        id: "base-2",
        name: "Platform Bed Frame",
        description: "Modern platform bed frame with wooden slats.",
        shortDescription: "Modern platform bed frame with wooden slats.",
        price: 450,
        image:
          "https://images.unsplash.com/photo-1588072683812-573523f769d9?q=80&w=1887&auto=format&fit=crop",
        images: [
          {
            url: "https://images.unsplash.com/photo-1588072683812-573523f769d9?q=80&w=1887&auto=format&fit=crop",
            alt: "Platform Bed Frame",
          },
        ],
        category: "bases",
        sku: "BAS-002",
        isActive: true,
        isFeatured: false,
        rating: { average: 4.5, count: 67 },
      },
      // Bundles
      {
        id: "bundle-1",
        name: "Complete Sleep Set",
        description:
          "Mattress, pillows, and bedding in one convenient package.",
        shortDescription: "Complete sleep solution in one package.",
        price: 1495,
        originalPrice: 1895,
        image:
          "https://images.unsplash.com/photo-1616627781431-d8a886095a74?q=80&w=1887&auto=format&fit=crop",
        images: [
          {
            url: "https://images.unsplash.com/photo-1616627781431-d8a886095a74?q=80&w=1887&auto=format&fit=crop",
            alt: "Complete Sleep Set",
          },
        ],
        category: "bundles",
        sku: "BUN-001",
        isActive: true,
        isFeatured: true,
        rating: { average: 4.9, count: 234 },
      },
      {
        id: "bundle-2",
        name: "Luxury Sleep Package",
        description: "Premium mattress, adjustable base, and luxury bedding.",
        shortDescription: "Premium mattress, base, and luxury bedding.",
        price: 1895,
        image:
          "https://images.unsplash.com/photo-1616627781431-d8a886095a74?q=80&w=1887&auto=format&fit=crop",
        images: [
          {
            url: "https://images.unsplash.com/photo-1616627781431-d8a886095a74?q=80&w=1887&auto=format&fit=crop",
            alt: "Luxury Sleep Package",
          },
        ],
        category: "bundles",
        sku: "BUN-002",
        isActive: true,
        isFeatured: false,
        rating: { average: 4.8, count: 167 },
      },
    ]);

    // Computed properties
    const categoryTitle = computed(() => {
      const category = route.query.category || selectedCategory.value;
      if (!category) return "All Products";

      const titles = {
        mattresses: "Mattresses",
        pillows: "Pillows",
        bedding: "Bedding",
        bases: "Bed Bases",
        bundles: "Sleep Bundles",
      };
      return titles[category] || "All Products";
    });

    const filteredProducts = computed(() => {
      // Use API products if available, otherwise fallback to static products
      const productsToFilter =
        productsStore.products.length > 0
          ? productsStore.products
          : staticProducts.value;
      let filtered = [...productsToFilter];

      // Filter by category
      if (selectedCategory.value) {
        filtered = filtered.filter(
          (product) => product.category === selectedCategory.value
        );
      }

      // Filter by search query
      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase();
        filtered = filtered.filter(
          (product) =>
            product.name.toLowerCase().includes(query) ||
            (product.shortDescription &&
              product.shortDescription.toLowerCase().includes(query)) ||
            (product.description &&
              product.description.toLowerCase().includes(query))
        );
      }

      // Filter by price range
      if (priceRange.value) {
        const [min, max] = priceRange.value.split("-").map(Number);
        filtered = filtered.filter((product) => {
          if (priceRange.value === "1000+") {
            return product.price >= 1000;
          }
          return product.price >= min && product.price <= max;
        });
      }

      // Sort products
      filtered.sort((a, b) => {
        switch (sortBy.value) {
          case "price-low":
            return a.price - b.price;
          case "price-high":
            return b.price - a.price;
          case "name":
            return a.name.localeCompare(b.name);
          case "featured":
          default:
            // Featured products first, then by rating
            if (a.isFeatured && !b.isFeatured) return -1;
            if (!a.isFeatured && b.isFeatured) return 1;
            return (b.rating?.average || 0) - (a.rating?.average || 0);
        }
      });

      return filtered;
    });

    // Methods
    const filterProducts = async () => {
      try {
        if (searchQuery.value) {
          await productsStore.searchProducts(searchQuery.value, {
            category: selectedCategory.value,
            priceRange: priceRange.value,
          });
        } else {
          await productsStore.fetchProducts({
            category: selectedCategory.value,
            priceRange: priceRange.value,
          });
        }
      } catch (error) {
        console.error("Error filtering products:", error);
        // Fallback to client-side filtering
      }
    };

    const addToCart = (product) => {
      if (product.isActive) {
        cartStore.addToCart(product);
        alert(`${product.name} added to cart!`);
      }
    };

    const loadProducts = async () => {
      try {
        await productsStore.fetchProducts({
          category: selectedCategory.value,
        });
      } catch (error) {
        console.error("Error loading products:", error);
        // Continue with static products as fallback
      }
    };

    const computeCategory = computed(() => route.query.category);

    watch(computeCategory, (val) => {
      selectedCategory.value = val;
      loadProducts();
    });

    // Initialize category from route and load products
    onMounted(async () => {
      if (route.query.category) {
        selectedCategory.value = route.query.category;
      }
      await loadProducts();
    });

    return {
      searchQuery,
      selectedCategory,
      priceRange,
      sortBy,
      categoryTitle,
      filteredProducts,
      filterProducts,
      addToCart,
      loading: productsStore.loading,
      error: productsStore.error,
    };
  },
};
</script>

<style scoped>
.products-page {
  min-height: 100vh;
}

.products-header {
  background-color: var(--light-gray-bg);
  padding: 60px 0;
  text-align: center;
}

.products-header h1 {
  margin-bottom: 1rem;
}

.filters-section {
  padding: 40px 0;
  border-bottom: 1px solid var(--border-color);
}

.filters-container {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  align-items: center;
}

.search-box {
  flex: 1;
  min-width: 200px;
}

.search-box input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  font-size: 1rem;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-group label {
  font-weight: 600;
  font-size: 0.9rem;
  color: var(--text-dark);
}

.filter-group select {
  padding: 8px 12px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 0.9rem;
  background-color: white;
}

.products-section {
  padding: 60px 0;
}

.loading,
.error,
.no-products {
  text-align: center;
  padding: 60px 0;
}

.loading h3,
.error h3,
.no-products h3 {
  margin-bottom: 1rem;
  color: var(--text-medium);
}

.error {
  color: #dc3545;
}

.error .btn {
  margin-top: 1rem;
  background-color: var(--primary-color);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
}

.error .btn:hover {
  background-color: var(--primary-dark);
}

.products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 30px;
  margin-top: 40px;
}

.product-card {
  background: var(--white);
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid var(--border-color);
  transition: box-shadow 0.3s ease, transform 0.3s ease;
  position: relative;
}

.product-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
}

.product-image {
  position: relative;
  overflow: hidden;
}

.product-card img {
  width: 100%;
  height: 250px;
  object-fit: cover;
  border-radius: 0;
  transition: transform 0.3s ease;
}

.product-card:hover img {
  transform: scale(1.05);
}

.featured-badge {
  position: absolute;
  top: 10px;
  left: 10px;
  background: var(--primary-color);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 600;
}

.discount-badge {
  position: absolute;
  top: 10px;
  right: 10px;
  background: #dc3545;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 600;
}

.product-card-content {
  padding: 25px;
}

.product-header {
  margin-bottom: 0.5rem;
}

.product-card h4 {
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.product-meta {
  display: flex;
  gap: 10px;
  font-size: 0.8rem;
  color: var(--text-medium);
  margin-bottom: 0.5rem;
}

.sku {
  font-family: monospace;
  background: var(--bg-light);
  padding: 2px 6px;
  border-radius: 3px;
}

.rating {
  color: #ffc107;
  font-weight: 600;
}

.product-card .description {
  color: var(--text-medium);
  font-size: 0.9rem;
  margin-bottom: 1rem;
  line-height: 1.5;
}

.pricing {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 1.5rem;
}

.current-price {
  color: var(--primary-color);
  font-weight: 700;
  font-size: 1.25rem;
}

.original-price {
  color: var(--text-medium);
  text-decoration: line-through;
  font-size: 1rem;
}

.product-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.product-actions .btn {
  flex: 1;
  min-width: 120px;
  font-size: 0.9rem;
  padding: 10px 16px;
}

.product-actions .btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .filters-container {
    flex-direction: column;
    align-items: stretch;
  }

  .search-box {
    min-width: auto;
  }

  .products-grid {
    grid-template-columns: 1fr;
  }

  .product-actions {
    flex-direction: column;
  }
}
</style>
